import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Card, CardBody, CardHeader, Table, Input, Button, Row, Col,
    Spinner, Alert, Badge, UncontrolledTooltip, ButtonGroup
} from 'reactstrap';
import { comptabiliteActions } from '../../sagas/comptabiliteSlice';
import { formatPrice } from '../../helpers/utility';
import {
    FaSearch, FaDownload, FaPrint, FaCalendarAlt,
    FaFileAlt, FaCoins, FaBalanceScale, FaFilter,
    FaInfoCircle, FaChartPie, FaExclamationTriangle
} from 'react-icons/fa';

const BalanceComptable = () => {
    const dispatch = useDispatch();
    const { balanceComptable, loading, error } = useSelector(state => state.comptabilite);
    const { company } = useSelector(state => state.auth);

    const [filters, setFilters] = useState({
        dateDebut: '',
        dateFin: '',
        filterType: 'all' // 'all', 'actif', 'passif'
    });

    const [totals, setTotals] = useState({
        totalDebit: 0,
        totalCredit: 0,
        totalSoldeActif: 0,
        totalSoldePassif: 0
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [filteredBalance, setFilteredBalance] = useState([]);

    useEffect(() => {
        // Load initial balance
        dispatch(comptabiliteActions.getBalanceComptable({}));
    }, [dispatch]);

    useEffect(() => {
        // Calculate totals when balance data changes
        if (balanceComptable && balanceComptable.length > 0) {
            const totalDebit = balanceComptable.reduce((sum, compte) => sum + (compte.totalDebit || 0), 0);
            const totalCredit = balanceComptable.reduce((sum, compte) => sum + (compte.totalCredit || 0), 0);

            // Calculate total solde actif and passif
            let totalSoldeActif = 0;
            let totalSoldePassif = 0;

            balanceComptable.forEach(compte => {
                // Calculer les soldes actif et passif selon le type de compte
                const soldeActuel = compte.soldeActuel || 0;

                if (soldeActuel > 0) {
                    // Si le solde est positif
                    if (compte.typeCompte === 'ACTIF' || compte.typeCompte === 'CHARGE') {
                        // Pour les comptes d'actif et de charge, un solde positif est débiteur
                        totalSoldeActif += soldeActuel;
                    } else if (compte.typeCompte === 'PASSIF' || compte.typeCompte === 'PRODUIT' || compte.typeCompte === 'CAPITAUX_PROPRES') {
                        // Pour les comptes de passif, produit et capitaux propres, un solde positif est créditeur
                        totalSoldePassif += soldeActuel;
                    }
                } else if (soldeActuel < 0) {
                    // Si le solde est négatif (valeur absolue)
                    const soldeAbsolu = Math.abs(soldeActuel);

                    if (compte.typeCompte === 'ACTIF' || compte.typeCompte === 'CHARGE') {
                        // Pour les comptes d'actif et de charge, un solde négatif est créditeur
                        totalSoldePassif += soldeAbsolu;
                    } else if (compte.typeCompte === 'PASSIF' || compte.typeCompte === 'PRODUIT' || compte.typeCompte === 'CAPITAUX_PROPRES') {
                        // Pour les comptes de passif, produit et capitaux propres, un solde négatif est débiteur
                        totalSoldeActif += soldeAbsolu;
                    }
                }
                // Si le solde est 0, il n'affecte ni l'actif ni le passif
            });

            setTotals({ totalDebit, totalCredit, totalSoldeActif, totalSoldePassif });
        }
    }, [balanceComptable]);

    useEffect(() => {
        // Filter and search balance data
        if (balanceComptable) {
            let filtered = [...balanceComptable];

            // Apply type filter
            if (filters.filterType === 'actif') {
                filtered = filtered.filter(compte => compte.typeCompte === 'ACTIF');
            } else if (filters.filterType === 'passif') {
                filtered = filtered.filter(compte => compte.typeCompte === 'PASSIF');
            }

            // Apply search term
            if (searchTerm) {
                const search = searchTerm.toLowerCase();
                filtered = filtered.filter(compte =>
                    compte.numeroCompte.toLowerCase().includes(search) ||
                    compte.intituleCompte.toLowerCase().includes(search)
                );
            }

            setFilteredBalance(filtered);
        }
    }, [balanceComptable, filters.filterType, searchTerm]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSearch = () => {
        dispatch(comptabiliteActions.getBalanceComptable({
            dateDebut: filters.dateDebut || null,
            dateFin: filters.dateFin || null
        }));
    };

    const exportToExcel = () => {
        // Implementation for Excel export
        console.log('Export to Excel functionality to be implemented');
    };

    const exportToPDF = () => {
        if (!balanceComptable || balanceComptable.length === 0) return;

        // Import libraries dynamically to avoid SSR issues
        import('jspdf').then(({ default: jsPDF }) => {
            import('jspdf-autotable').then(({ default: autoTable }) => {
                // Récupérer les informations de l'entreprise depuis le store Redux
                const entrepriseInfo = company || {
                    name: 'Votre Entreprise',
                    subtitle: 'Sous-titre de l\'entreprise',
                    address: 'Adresse de l\'entreprise',
                    phone: 'Téléphone',
                    email: 'email@entreprise.com',
                    ice: 'ICE: 000000000000000',
                    logo: null // URL du logo si disponible
                };

                // Create a new PDF document
                const doc = new jsPDF();
                const pageWidth = doc.internal.pageSize.width;
                const centerX = pageWidth / 2;

                // Define colors
                const primaryColor = [41, 128, 185]; // Blue
                const secondaryColor = [100, 100, 100]; // Dark gray

                // Start position for company info
                let yPos = 15;

                // Add company header - Centered
                if (entrepriseInfo.logo) {
                    try {
                        // Calculate logo position to center the entire header block
                        const logoWidth = 30;
                        const logoHeight = 30;
                        const logoX = centerX - (logoWidth / 2);

                        // Add logo
                        doc.addImage(entrepriseInfo.logo, 'JPEG', logoX, yPos, logoWidth, logoHeight);
                        yPos += logoHeight + 5;

                        // Add company name (centered, bold, uppercase)
                        doc.setFont(undefined, 'bold');
                        doc.setFontSize(16);
                        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
                        doc.text(entrepriseInfo.name.toUpperCase(), centerX, yPos, { align: 'center' });
                        yPos += 7;

                        // Add subtitle (centered)
                        if (entrepriseInfo.subtitle) {
                            doc.setFont(undefined, 'italic');
                            doc.setFontSize(10);
                            doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
                            doc.text(entrepriseInfo.subtitle, centerX, yPos, { align: 'center' });
                            yPos += 7;
                        }
                    } catch (error) {
                        console.error("Erreur lors de l'ajout du logo:", error);
                        // Fallback if logo fails to load
                        doc.setFont(undefined, 'bold');
                        doc.setFontSize(18);
                        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
                        doc.text(entrepriseInfo.name.toUpperCase(), centerX, yPos, { align: 'center' });
                        yPos += 8;

                        if (entrepriseInfo.subtitle) {
                            doc.setFont(undefined, 'italic');
                            doc.setFontSize(11);
                            doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
                            doc.text(entrepriseInfo.subtitle, centerX, yPos, { align: 'center' });
                            yPos += 8;
                        }
                    }
                } else {
                    // No logo, just text
                    doc.setFont(undefined, 'bold');
                    doc.setFontSize(18);
                    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
                    doc.text(entrepriseInfo.name.toUpperCase(), centerX, yPos, { align: 'center' });
                    yPos += 8;

                    if (entrepriseInfo.subtitle) {
                        doc.setFont(undefined, 'italic');
                        doc.setFontSize(11);
                        doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
                        doc.text(entrepriseInfo.subtitle, centerX, yPos, { align: 'center' });
                        yPos += 8;
                    }
                }

                // Add company contact details (centered)
                doc.setFont(undefined, 'normal');
                doc.setFontSize(9);
                doc.setTextColor(80, 80, 80);

                if (entrepriseInfo.address) {
                    doc.text(entrepriseInfo.address, centerX, yPos, { align: 'center' });
                    yPos += 5;
                }

                // Contact info in a single line
                let contactInfo = [];
                if (entrepriseInfo.phone) contactInfo.push(`Tél: ${entrepriseInfo.phone}`);
                if (entrepriseInfo.email) contactInfo.push(`Email: ${entrepriseInfo.email}`);
                if (entrepriseInfo.ice) contactInfo.push(`ICE: ${entrepriseInfo.ice}`);

                doc.text(contactInfo.join(' | '), centerX, yPos, { align: 'center' });
                yPos += 5;

                // Add horizontal line with gradient effect
                const lineY = yPos + 5;
                doc.setDrawColor(220, 220, 220);
                doc.setLineWidth(0.1);
                doc.line(20, lineY, pageWidth - 20, lineY);

                doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
                doc.setLineWidth(0.5);
                doc.line(40, lineY + 0.5, pageWidth - 40, lineY + 0.5);

                doc.setDrawColor(220, 220, 220);
                doc.setLineWidth(0.1);
                doc.line(20, lineY + 1, pageWidth - 20, lineY + 1);

                // Update yPos for the title
                yPos = lineY + 15;

                // Add title with background
                doc.setFillColor(245, 245, 245);
                doc.rect(20, yPos - 8, pageWidth - 40, 16, 'F');

                doc.setFontSize(16);
                doc.setFont(undefined, 'bold');
                doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
                doc.text('BALANCE COMPTABLE', centerX, yPos, { align: 'center' });

                // Reset text color
                doc.setTextColor(0, 0, 0);
                doc.setFont(undefined, 'normal');

                // Add period information if available
                yPos += 15;
                if (filters.dateDebut || filters.dateFin) {
                    doc.setFontSize(10);
                    const dateRange = `Période: ${filters.dateDebut ? new Date(filters.dateDebut).toLocaleDateString('fr-DZ') : 'Début'} au ${filters.dateFin ? new Date(filters.dateFin).toLocaleDateString('fr-DZ') : 'Aujourd\'hui'}`;
                    doc.text(dateRange, centerX, yPos, { align: 'center' });
                    yPos += 10;
                }

                // Add summary information
                doc.setFillColor(250, 250, 250);
                doc.roundedRect(20, yPos - 5, pageWidth - 40, 30, 2, 2, 'F');

                doc.setFontSize(10);
                doc.setFont(undefined, 'bold');

                // Summary row with 4 columns
                const colWidth = (pageWidth - 50) / 4;

                // Headers
                doc.text("Total Débit", 30, yPos + 5);
                doc.text("Total Crédit", 30 + colWidth, yPos + 5);
                doc.text("Total Solde Actif", 30 + colWidth * 2, yPos + 5);
                doc.text("Total Solde Passif", 30 + colWidth * 3, yPos + 5);

                // Values
                doc.setFont(undefined, 'normal');
                doc.setTextColor(230, 126, 34); // Orange for debit
                doc.text(formatPrice(totals.totalDebit), 30, yPos + 15);

                doc.setTextColor(52, 152, 219); // Blue for credit
                doc.text(formatPrice(totals.totalCredit), 30 + colWidth, yPos + 15);

                doc.setTextColor(46, 204, 113); // Green for active balance
                doc.text(formatPrice(totals.totalSoldeActif), 30 + colWidth * 2, yPos + 15);

                doc.setTextColor(41, 128, 185); // Blue for passive balance
                doc.text(formatPrice(totals.totalSoldePassif), 30 + colWidth * 3, yPos + 15);

                // Reset text color
                doc.setTextColor(0, 0, 0);

                // Add balance status
                yPos += 35;
                doc.setFontSize(11);
                doc.setFont(undefined, 'bold');

                const isBalanced = totals.totalDebit === totals.totalCredit;

                if (isBalanced) {
                    doc.setTextColor(46, 204, 113); // Green for balanced
                    doc.text("BALANCE ÉQUILIBRÉE", centerX, yPos, { align: 'center' });
                } else {
                    doc.setTextColor(231, 76, 60); // Red for unbalanced
                    doc.text("BALANCE DÉSÉQUILIBRÉE - Écart: " + formatPrice(Math.abs(totals.totalDebit - totals.totalCredit)), centerX, yPos, { align: 'center' });
                }

                // Reset text color
                doc.setTextColor(0, 0, 0);
                doc.setFont(undefined, 'normal');

                // Add accounts table
                yPos += 15;

                // Table header
                const headers = [
                    'N° Compte',
                    'Intitulé',
                    'Classe',
                    'Type',
                    'Solde Initial',
                    'Total Débit',
                    'Total Crédit',
                    'Solde Actuel'
                ];

                // Table data
                const data = filteredBalance.map(compte => [
                    compte.numeroCompte,
                    compte.intituleCompte,
                    compte.classeCompte,
                    compte.typeCompte,
                    formatPrice(compte.soldeInitial),
                    formatPrice(compte.totalDebit),
                    formatPrice(compte.totalCredit),
                    formatPrice(compte.soldeActuel)
                ]);

                // Add totals row
                data.push([
                    '',
                    'TOTAUX',
                    '',
                    '',
                    '',
                    formatPrice(totals.totalDebit),
                    formatPrice(totals.totalCredit),
                    isBalanced ? 'ÉQUILIBRÉE' : 'DÉSÉQUILIBRÉE'
                ]);

                // Add accounts table with improved styling
                autoTable(doc, {
                    startY: yPos,
                    head: [headers],
                    body: data,
                    theme: 'striped',
                    headStyles: {
                        fillColor: primaryColor,
                        textColor: 255,
                        fontStyle: 'bold'
                    },
                    columnStyles: {
                        0: { // N° Compte
                            cellWidth: 25,
                            fontStyle: 'bold',
                            fontSize: 8
                        },
                        1: { // Intitulé
                            cellWidth: 'auto',
                            fontSize: 8
                        },
                        2: { // Classe
                            cellWidth: 20,
                            fontSize: 8,
                            halign: 'center'
                        },
                        3: { // Type
                            cellWidth: 20,
                            fontSize: 8,
                            halign: 'center'
                        },
                        4: { // Solde Initial
                            cellWidth: 25,
                            halign: 'right',
                            fontSize: 8
                        },
                        5: { // Total Débit
                            cellWidth: 25,
                            halign: 'right',
                            fontSize: 8
                        },
                        6: { // Total Crédit
                            cellWidth: 25,
                            halign: 'right',
                            fontSize: 8
                        },
                        7: { // Solde Actuel
                            cellWidth: 25,
                            halign: 'right',
                            fontStyle: 'bold',
                            fontSize: 8
                        }
                    },
                    alternateRowStyles: {
                        fillColor: [248, 248, 248]
                    },
                    // Style for the last row (totals)
                    didParseCell: function (data) {
                        const lastRow = data.table.body.length - 1;
                        if (data.row.index === lastRow) {
                            data.cell.styles.fontStyle = 'bold';
                            data.cell.styles.fillColor = [240, 240, 240];

                            // Special styling for the "ÉQUILIBRÉE" or "DÉSÉQUILIBRÉE" cell
                            if (data.column.index === 7) {
                                if (isBalanced) {
                                    data.cell.styles.textColor = [46, 204, 113]; // Green
                                } else {
                                    data.cell.styles.textColor = [231, 76, 60]; // Red
                                }
                            }
                        }

                        // Style for Type column
                        if (data.column.index === 3 && data.row.index !== lastRow) {
                            const typeCompte = data.cell.text[0];
                            if (typeCompte === 'ACTIF') {
                                data.cell.styles.textColor = [46, 204, 113]; // Green
                            } else if (typeCompte === 'PASSIF') {
                                data.cell.styles.textColor = [52, 152, 219]; // Blue
                            } else if (typeCompte === 'CHARGE') {
                                data.cell.styles.textColor = [230, 126, 34]; // Orange
                            } else if (typeCompte === 'PRODUIT') {
                                data.cell.styles.textColor = [142, 68, 173]; // Purple
                            }
                        }

                        // Style for Solde Actuel column
                        if (data.column.index === 7 && data.row.index !== lastRow) {
                            const soldeText = data.cell.text[0];
                            const soldeValue = parseFloat(soldeText.replace(/[^\d.-]/g, ''));

                            if (soldeValue > 0) {
                                data.cell.styles.textColor = [46, 204, 113]; // Green for positive
                            } else if (soldeValue < 0) {
                                data.cell.styles.textColor = [231, 76, 60]; // Red for negative
                            }
                        }

                        // Style for Total Débit column
                        if (data.column.index === 5 && data.row.index !== lastRow) {
                            const debitText = data.cell.text[0];
                            const debitValue = parseFloat(debitText.replace(/[^\d.-]/g, ''));

                            if (debitValue > 0) {
                                data.cell.styles.textColor = [230, 126, 34]; // Orange for debit
                            }
                        }

                        // Style for Total Crédit column
                        if (data.column.index === 6 && data.row.index !== lastRow) {
                            const creditText = data.cell.text[0];
                            const creditValue = parseFloat(creditText.replace(/[^\d.-]/g, ''));

                            if (creditValue > 0) {
                                data.cell.styles.textColor = [52, 152, 219]; // Blue for credit
                            }
                        }
                    }
                });

                // Add footer with date and page numbers
                const pageCount = doc.internal.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);

                    // Add a subtle footer background
                    doc.setFillColor(248, 248, 248);
                    doc.rect(0, doc.internal.pageSize.height - 20, doc.internal.pageSize.width, 20, 'F');

                    // Add a thin line above footer
                    doc.setDrawColor(220, 220, 220);
                    doc.setLineWidth(0.5);
                    doc.line(20, doc.internal.pageSize.height - 20, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 20);

                    // Add date and time
                    doc.setFontSize(8);
                    doc.setTextColor(100, 100, 100);
                    doc.setFont(undefined, 'italic');

                    const now = new Date().toLocaleDateString('fr-DZ') + ' ' + new Date().toLocaleTimeString('fr-DZ');
                    doc.text(`Généré le: ${now}`, 25, doc.internal.pageSize.height - 10);

                    // Add page number
                    doc.setFont(undefined, 'bold');
                    doc.text(`Page ${i} sur ${pageCount}`, doc.internal.pageSize.width - 25, doc.internal.pageSize.height - 10, { align: 'right' });

                    // Add company name in footer (very subtle)
                    doc.setTextColor(180, 180, 180);
                    doc.setFontSize(7);
                    doc.setFont(undefined, 'normal');
                    doc.text(entrepriseInfo.name, centerX, doc.internal.pageSize.height - 10, { align: 'center' });
                }

                // Open PDF in a new tab instead of downloading
                const pdfBlob = doc.output('blob');
                const pdfUrl = URL.createObjectURL(pdfBlob);
                window.open(pdfUrl, '_blank');
            });
        });
    };



    // Get class color based on account class
    const getClassColor = (classeCompte) => {
        switch (classeCompte?.charAt(0)) {
            case '1': return 'primary'; // Comptes de capitaux
            case '2': return 'info';    // Comptes d'immobilisations
            case '3': return 'success'; // Comptes de stocks
            case '4': return 'warning'; // Comptes de tiers
            case '5': return 'danger';  // Comptes financiers
            case '6': return 'dark';    // Comptes de charges
            case '7': return 'secondary'; // Comptes de produits
            default: return 'light';
        }
    };

    // Get badge color based on account type
    const getTypeColor = (typeCompte) => {
        switch (typeCompte) {
            case 'ACTIF': return 'success';
            case 'PASSIF': return 'info';
            case 'CHARGE': return 'warning';
            case 'PRODUIT': return 'primary';
            default: return 'secondary';
        }
    };

    return (
        <div className="page-content" style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-1">
                        <FaBalanceScale className="me-2 text-primary" />
                        Balance Comptable
                    </h4>
                    <p className="text-muted mb-0 small">
                        Consultez les soldes et mouvements de tous vos comptes
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <Button color="outline-secondary" onClick={exportToPDF} disabled={!balanceComptable?.length} size="sm">
                        <FaPrint className="me-1" /> PDF
                    </Button>
                    <Button color="outline-success" onClick={exportToExcel} disabled={!balanceComptable?.length} size="sm">
                        <FaDownload className="me-1" /> Excel
                    </Button>
                </div>
            </div>

            {/* Filters Card */}
            <Card className="shadow-sm mb-3 flex-shrink-0">
                <CardBody className="py-3">
                    <Row className="g-3 align-items-end">
                        <Col lg={3}>
                            <label className="form-label fw-semibold small">
                                <FaCalendarAlt className="me-1" />
                                Date Début
                            </label>
                            <Input
                                type="date"
                                value={filters.dateDebut}
                                onChange={(e) => handleFilterChange('dateDebut', e.target.value)}
                                className="form-control-sm"
                            />
                        </Col>
                        <Col lg={3}>
                            <label className="form-label fw-semibold small">
                                <FaCalendarAlt className="me-1" />
                                Date Fin
                            </label>
                            <Input
                                type="date"
                                value={filters.dateFin}
                                onChange={(e) => handleFilterChange('dateFin', e.target.value)}
                                className="form-control-sm"
                            />
                        </Col>
                        <Col lg={2}>
                            <label className="form-label fw-semibold small">
                                <FaFilter className="me-1" />
                                Type
                            </label>
                            <ButtonGroup size="sm" className="w-100">
                                <Button
                                    color={filters.filterType === 'all' ? 'primary' : 'outline-primary'}
                                    onClick={() => handleFilterChange('filterType', 'all')}
                                >
                                    Tous
                                </Button>
                                <Button
                                    color={filters.filterType === 'actif' ? 'success' : 'outline-success'}
                                    onClick={() => handleFilterChange('filterType', 'actif')}
                                >
                                    Actif
                                </Button>
                                <Button
                                    color={filters.filterType === 'passif' ? 'info' : 'outline-info'}
                                    onClick={() => handleFilterChange('filterType', 'passif')}
                                >
                                    Passif
                                </Button>
                            </ButtonGroup>
                        </Col>
                        <Col lg={2}>
                            <label className="form-label fw-semibold small">
                                <FaSearch className="me-1" />
                                Recherche
                            </label>
                            <Input
                                type="text"
                                placeholder="Rechercher..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="form-control-sm"
                            />
                        </Col>
                        <Col lg={2}>
                            <Button
                                color="primary"
                                onClick={handleSearch}
                                disabled={loading.balanceComptable}
                                className="w-100"
                                size="sm"
                            >
                                {loading.balanceComptable ? (
                                    <>
                                        <Spinner size="sm" className="me-1" />
                                        Chargement...
                                    </>
                                ) : (
                                    <>
                                        <FaSearch className="me-1" />
                                        Rechercher
                                    </>
                                )}
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Error Display */}
            {error && (
                <Alert color="danger" className="shadow-sm mb-3 flex-shrink-0">
                    <div className="d-flex align-items-center">
                        <FaExclamationTriangle className="me-2" />
                        {error}
                    </div>
                </Alert>
            )}

            {/* Summary Cards */}
            {balanceComptable && balanceComptable.length > 0 && (
                <Row className="mb-3 g-3 flex-shrink-0">
                    <Col md={3}>
                        <Card className="shadow-sm h-100 border-0">
                            <CardBody className="p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="text-muted small">Total Débit</div>
                                        <h5 className="mb-0 text-warning">{formatPrice(totals.totalDebit)}</h5>
                                    </div>
                                    <div className="rounded-circle bg-warning bg-opacity-10 p-3">
                                        <FaCoins className="text-warning" />
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="shadow-sm h-100 border-0">
                            <CardBody className="p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="text-muted small">Total Crédit</div>
                                        <h5 className="mb-0 text-info">{formatPrice(totals.totalCredit)}</h5>
                                    </div>
                                    <div className="rounded-circle bg-info bg-opacity-10 p-3">
                                        <FaCoins className="text-info" />
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="shadow-sm h-100 border-0">
                            <CardBody className="p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="text-muted small">Total Solde Actif</div>
                                        <h5 className="mb-0 text-success">{formatPrice(totals.totalSoldeActif)}</h5>
                                    </div>
                                    <div className="rounded-circle bg-success bg-opacity-10 p-3">
                                        <FaChartPie className="text-success" />
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="shadow-sm h-100 border-0">
                            <CardBody className="p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="text-muted small">Total Solde Passif</div>
                                        <h5 className="mb-0 text-primary">{formatPrice(totals.totalSoldePassif)}</h5>
                                    </div>
                                    <div className="rounded-circle bg-primary bg-opacity-10 p-3">
                                        <FaChartPie className="text-primary" />
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Balance Table */}
            {balanceComptable && balanceComptable.length > 0 ? (
                <Card className="shadow-sm flex-grow-1 d-flex flex-column overflow-hidden">
                    <CardHeader className="bg-light py-2 d-flex justify-content-between align-items-center flex-shrink-0">
                        <div className="d-flex align-items-center">
                            <FaFileAlt className="me-2 text-primary" />
                            <h6 className="mb-0">Balance des Comptes</h6>
                        </div>
                        <div className="d-flex align-items-center">
                            <Badge color="primary" pill className="me-2">
                                {filteredBalance.length} compte(s)
                            </Badge>
                            <span className={`badge ${totals.totalDebit === totals.totalCredit ? 'bg-success' : 'bg-warning'}`}>
                                {totals.totalDebit === totals.totalCredit ? 'ÉQUILIBRÉE' : 'DÉSÉQUILIBRÉE'}
                            </span>
                            {totals.totalDebit !== totals.totalCredit && (
                                <span id="balanceEcart" className="ms-2 text-danger small">
                                    <FaInfoCircle />
                                </span>
                            )}
                            {totals.totalDebit !== totals.totalCredit && (
                                <UncontrolledTooltip placement="top" target="balanceEcart">
                                    Écart: {formatPrice(Math.abs(totals.totalDebit - totals.totalCredit))}
                                </UncontrolledTooltip>
                            )}
                        </div>
                    </CardHeader>
                    <CardBody className="p-0 flex-grow-1 overflow-hidden">
                        <div className="table-responsive h-100" style={{ overflowY: 'auto' }}>
                            <Table responsive striped hover className="mb-0 table-sm">
                                <thead className="table-dark sticky-top">
                                    <tr>
                                        <th style={{ minWidth: '100px' }}>N° Compte</th>
                                        <th style={{ minWidth: '250px' }}>Intitulé</th>
                                        <th style={{ minWidth: '80px' }}>Classe</th>
                                        <th style={{ minWidth: '80px' }}>Type</th>
                                        <th className="text-end" style={{ minWidth: '120px' }}>Solde Initial</th>
                                        <th className="text-end" style={{ minWidth: '120px' }}>Total Débit</th>
                                        <th className="text-end" style={{ minWidth: '120px' }}>Total Crédit</th>
                                        <th className="text-end" style={{ minWidth: '120px' }}>Solde Actuel</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredBalance.map((compte, index) => (
                                        <tr key={index} className="align-middle">
                                            <td>
                                                <span className="fw-bold">{compte.numeroCompte}</span>
                                            </td>
                                            <td>
                                                <div className="text-truncate" style={{ maxWidth: '250px' }} title={compte.intituleCompte}>
                                                    {compte.intituleCompte}
                                                </div>
                                            </td>
                                            <td>
                                                <Badge
                                                    color={getClassColor(compte.classeCompte)}
                                                    className="badge-soft"
                                                    title={`Classe ${compte.classeCompte?.charAt(0)}`}
                                                >
                                                    {compte.classeCompte}
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge color={getTypeColor(compte.typeCompte)}>
                                                    {compte.typeCompte}
                                                </Badge>
                                            </td>
                                            <td className="text-end">
                                                <span className={compte.soldeInitial !== 0 ? 'fw-medium' : 'text-muted'}>
                                                    {formatPrice(compte.soldeInitial)}
                                                </span>
                                            </td>
                                            <td className="text-end">
                                                {compte.totalDebit > 0 ? (
                                                    <span className="text-warning fw-medium">
                                                        {formatPrice(compte.totalDebit)}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td className="text-end">
                                                {compte.totalCredit > 0 ? (
                                                    <span className="text-info fw-medium">
                                                        {formatPrice(compte.totalCredit)}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td className="text-end">
                                                <strong className={compte.soldeActuel > 0 ? 'text-success' : compte.soldeActuel < 0 ? 'text-danger' : 'text-muted'}>
                                                    {formatPrice(compte.soldeActuel)}
                                                </strong>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-dark">
                                    <tr>
                                        <th colSpan="5">TOTAUX</th>
                                        <th className="text-end">{formatPrice(totals.totalDebit)}</th>
                                        <th className="text-end">{formatPrice(totals.totalCredit)}</th>
                                        <th className="text-end">
                                            <span className={totals.totalDebit === totals.totalCredit ? 'text-success' : 'text-warning'}>
                                                {totals.totalDebit === totals.totalCredit ? 'ÉQUILIBRÉE' : 'DÉSÉQUILIBRÉE'}
                                            </span>
                                        </th>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    </CardBody>
                </Card>
            ) : (
                <>
                    {/* Loading State */}
                    {loading.balanceComptable ? (
                        <Card className="shadow-sm flex-grow-1">
                            <CardBody className="text-center py-5">
                                <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                                <div className="mt-3">
                                    <h6>Chargement de la balance comptable...</h6>
                                    <p className="text-muted mb-0">Veuillez patienter</p>
                                </div>
                            </CardBody>
                        </Card>
                    ) : (
                        /* Empty State */
                        <Card className="shadow-sm flex-grow-1">
                            <CardBody className="text-center py-5">
                                <div className="text-muted">
                                    <FaFileAlt size={48} className="mb-3 opacity-50" />
                                    <h5>Aucune donnée disponible</h5>
                                    <p className="mb-0">Aucun compte trouvé pour la période sélectionnée.</p>
                                </div>
                            </CardBody>
                        </Card>
                    )}
                </>
            )}

            {/* Summary Card - Only show when data is available and not loading */}
            {balanceComptable && balanceComptable.length > 0 && !loading.balanceComptable && (
                <Card className="shadow-sm mt-3 flex-shrink-0">
                    <CardBody className="p-3">
                        <Row>
                            <Col md={6}>
                                <h6 className="mb-3 d-flex align-items-center">
                                    <FaInfoCircle className="me-2 text-primary" />
                                    Résumé de la Balance
                                </h6>
                                <div className="d-flex flex-wrap gap-4">
                                    <div>
                                        <div className="text-muted small">Nombre de comptes</div>
                                        <h5 className="mb-0">{balanceComptable.length}</h5>
                                    </div>
                                    <div>
                                        <div className="text-muted small">Total Débit</div>
                                        <h5 className="mb-0 text-warning">{formatPrice(totals.totalDebit)}</h5>
                                    </div>
                                    <div>
                                        <div className="text-muted small">Total Crédit</div>
                                        <h5 className="mb-0 text-info">{formatPrice(totals.totalCredit)}</h5>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6}>
                                <h6 className="mb-3 d-flex align-items-center">
                                    <FaBalanceScale className="me-2 text-primary" />
                                    Statut
                                </h6>
                                <div className="d-flex flex-column">
                                    <div className="mb-2">
                                        <Badge
                                            color={totals.totalDebit === totals.totalCredit ? 'success' : 'danger'}
                                            className="p-2 fs-6"
                                        >
                                            {totals.totalDebit === totals.totalCredit ? 'Balance équilibrée' : 'Balance déséquilibrée'}
                                        </Badge>
                                    </div>
                                    {totals.totalDebit !== totals.totalCredit && (
                                        <div className="d-flex align-items-center">
                                            <FaExclamationTriangle className="text-danger me-2" />
                                            <div>
                                                <div className="text-muted small">Écart</div>
                                                <h5 className="mb-0 text-danger">{formatPrice(Math.abs(totals.totalDebit - totals.totalCredit))}</h5>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            )}

            <style jsx>{`
                .badge-soft {
                    font-weight: 500;
                    padding: 0.35em 0.65em;
                }
                .table-hover tbody tr:hover {
                    background-color: rgba(0,123,255,.075);
                }
                .sticky-top {
                    position: sticky;
                    top: 0;
                    z-index: 1020;
                }
                .table-sm td,
                .table-sm th {
                    padding: 0.5rem;
                }
                /* Custom scrollbar for webkit browsers */
                .table-responsive::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                .table-responsive::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 4px;
                }
                .table-responsive::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 4px;
                }
                .table-responsive::-webkit-scrollbar-thumb:hover {
                    background: #a8a8a8;
                }
                /* Ensure proper spacing */
                .page-content {
                    padding: 1rem;
                }
                /* Make the table take remaining space */
                .flex-grow-1 {
                    flex-grow: 1 !important;
                }
                .flex-shrink-0 {
                    flex-shrink: 0 !important;
                }
                .overflow-hidden {
                    overflow: hidden !important;
                }
                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .page-content {
                        padding: 0.5rem;
                    }
                    .table-responsive {
                        font-size: 0.875rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default BalanceComptable;

